import { atomicWeights } from '../data/atomic-weights.ts';
import type { CasOrCategory, ProductID } from '../types/db.ts';
import type { ProductWithChemicalsForUsageMix, ReleaseProfileProcessChemical } from '../types/tri.ts';
import type { UsageMixComputationResult } from '../types/release-profiles.ts';

/**
 * Takes molecular formulas of the format `(Element Number?)+` (example: "H2O") and returns the total grams per mole of
 * the chemical.
 */
export function molecularFormulaToGramsPerMole(
  formula: string,
): { status: 'success'; value: number } | { status: 'error'; error: string } {
  let result = 0;
  const pieces = formula.split(/(?=[A-Z]+[^A-Z]?)/);

  for (const piece of pieces) {
    const split = /^([A-Za-z]+)(\d*)$/.exec(piece);
    if (!split) {
      return { status: 'error', error: `Unable to extract atom count from formula piece "${piece}" in "${formula}".` };
    }

    const [_, element, number] = split;
    let multiplier: number;
    if (number === '') {
      multiplier = 1;
    } else {
      multiplier = parseInt(number, 10);
    }
    let weight = atomicWeights[element];
    if (!weight) {
      return { status: 'error', error: `molecularFormulaToGramsPerMole: Unknown element: ${element}` };
    }
    weight *= multiplier;
    result += weight;
  }

  return { status: 'success', value: result };
}

/**
 * Takes molecular formulas of the format `(Element Number?)+` (example: "H2O") and returns the total grams per mole of
 * the chemical. Throws an error if your formula is invalid.
 *
 * @see molecularFormulaToGramsPerMole
 * @throws Error
 */
export function molecularFormulaToGramsPerMoleOrThrow(formula: string): number {
  const result = molecularFormulaToGramsPerMole(formula);
  if (result.status === 'success') {
    return result.value;
  } else {
    throw new Error(`molecularFormulaToGramsPerMoleOrThrow: ${formula}: ${result.error}`);
  }
}

/**
 * Counts each type of atom in a molecular formula. i.e. "H2O" yields `{H: 2, O: 1}`.
 */
export function molecularFormulaCountElements(formula: string): Partial<Record<string, number>> {
  const result: Partial<Record<string, number>> = {};
  const pieces = formula.split(/(?=[A-Z]+[^A-Z]?)/);

  for (const piece of pieces) {
    const split = /^([A-Za-z]+)(\d*)$/.exec(piece);
    if (!split) {
      continue;
    }

    const [_, element, number] = split;
    let multiplier: number;
    if (number === '') {
      multiplier = 1;
    } else {
      multiplier = parseInt(number, 10);
    }

    if (result[element]) {
      result[element] += multiplier;
    } else {
      result[element] = multiplier;
    }
  }

  return result;
}

/**
 * Given a compound formula like "Ag2O" returns 1/2 for the element "Ag", or 1/1 for the element "O".
 */
export function moleRatioFromElementAndFormulas(args: { element: string; compoundFormula: string }): number {
  return moleRatioFromTwoCompounds({ baseFormula: args.element, compoundFormula: args.compoundFormula });
}

/**
 * Given a compound formula like "CaN2O6" returns 1/2 for the compound "NO3".
 */
export function moleRatioFromTwoCompounds(args: { baseFormula: string; compoundFormula: string }): number {
  const baseCounts = molecularFormulaCountElements(args.baseFormula);
  const compoundCounts = molecularFormulaCountElements(args.compoundFormula);

  const baseElements = Object.keys(baseCounts);
  if (baseElements.length === 0) {
    return NaN;
  }

  let count = 0;
  outer: while (true) {
    for (const element of baseElements) {
      const baseCount = baseCounts[element] ?? 0;
      const compoundCount = compoundCounts[element] ?? 0;
      if (compoundCount <= 0) {
        break outer;
      }

      compoundCounts[element] = compoundCount - baseCount;
    }

    count += 1;
  }

  // Couldn't find the base compound at all, the count is nonsensical:
  if (count === 0) {
    return NaN;
  }

  return 1 / count;
}

/**
 * Given the concentration of a compound in a product, converts to the concentration of the relevant element. Useful
 * for computing metal and nitrate releases, where the compounds are used for threshold calculations, but the elemental
 * forms are used for actual release calculations.
 */
export function compoundToElementalConcentration(args: {
  compoundConcentration: number;
  compoundGramsPerMole: number;
  elementGramsPerMole: number;
  moleRatio: number;
}): number {
  return args.compoundConcentration / ((args.compoundGramsPerMole / args.elementGramsPerMole) * args.moleRatio);
}

export function elementalToCompoundConcentration(args: {
  elementalConcentration: number;
  compoundGramsPerMole: number;
  elementGramsPerMole: number;
  moleRatio: number;
}): number {
  return args.elementalConcentration / (args.elementGramsPerMole / args.compoundGramsPerMole / args.moleRatio);
}

const categoryToElement: Partial<Record<CasOrCategory, string>> = {
  N020: 'As', // Arsenic compounds
  N090: 'Cr', // Chromium compounds
  N096: 'Co', // Cobalt compounds
  N420: 'Pb', // Lead compounds
  N458: 'Hg', // Mercury compounds
  N010: 'Sb', // Antimony compounds
  N040: 'Ba', // Barium compounds
  N050: 'Be', // Beryllium compounds
  N078: 'Cd', // Cadmium compounds
  N100: 'Cu', // Copper compounds
  N450: 'Mn', // Manganese compounds
  N495: 'Ni', // Nickel compounds
  N725: 'Se', // Selenium compounds
  N740: 'Ag', // Silver compounds
  N760: 'Tl', // Thallium compounds
  N770: 'V', // Vanadium compounds
  N982: 'Zn', // Zinc compounds
  // Not a single element, handle with care:
  N511: 'NO3', // Nitrate compounds
} as Partial<Record<CasOrCategory, string>>;

export const categoryToElementCas: Partial<Record<CasOrCategory, CasOrCategory>> = {
  N010: '7440-36-0', // Antimony
  N020: '7440-38-2', // Arsenic
  N040: '7440-39-3', // Barium
  N050: '7440-41-7', // Beryllium
  N078: '7440-43-9', // Cadmium
  N090: '7440-47-3', // Chromium
  N096: '7440-48-4', // Cobalt
  N100: '7440-50-8', // Copper
  N420: '7439-92-1', // Lead
  N450: '7439-96-5', // Manganese
  N458: '7439-97-6', // Mercury
  N495: '7440-02-0', // Nickel
  N725: '7782-49-2', // Selenium
  N740: '7440-22-4', // Silver
  N760: '7440-28-0', // Thallium
  N770: '7440-62-2', // Vanadium
  N982: '7440-66-6', // Zinc
} as Partial<Record<CasOrCategory, CasOrCategory>>;

export const elementCasToCategory: Partial<Record<CasOrCategory, CasOrCategory>> = {
  '7440-36-0': 'N010', // Antimony
  '7440-38-2': 'N020', // Arsenic
  '7440-39-3': 'N040', // Barium
  '7440-41-7': 'N050', // Beryllium
  '7440-43-9': 'N078', // Cadmium
  '7440-47-3': 'N090', // Chromium
  '7440-48-4': 'N096', // Cobalt
  '7440-50-8': 'N100', // Copper
  '7439-92-1': 'N420', // Lead
  '7439-96-5': 'N450', // Manganese
  '7439-97-6': 'N458', // Mercury
  '7440-02-0': 'N495', // Nickel
  '7782-49-2': 'N725', // Selenium
  '7440-22-4': 'N740', // Silver
  '7440-28-0': 'N760', // Thallium
  '7440-62-2': 'N770', // Vanadium
  '7440-66-6': 'N982', // Zinc
} as Partial<Record<CasOrCategory, CasOrCategory>>;

// This set is used to determine default POTW fate distributions for 6.1, as well as valid waste type codes for 6.2:
export const metalsSet = new Set([
  'N010', // Antimony
  'N020', // Arsenic
  'N040', // Barium
  'N050', // Beryllium
  'N078', // Cadmium
  'N090', // Chromium
  'N096', // Cobalt
  'N100', // Copper
  'N420', // Lead
  'N450', // Manganese
  'N458', // Mercury
  'N495', // Nickel
  'N725', // Selenium
  'N740', // Silver
  'N760', // Thallium
  'N770', // Vanadium
  'N982', // Zinc
  '7440-36-0', // Antimony
  '7440-38-2', // Arsenic
  '7440-39-3', // Barium
  '7440-41-7', // Beryllium
  '7440-43-9', // Cadmium
  '7440-47-3', // Chromium
  '7440-48-4', // Cobalt
  '7440-50-8', // Copper
  '7439-92-1', // Lead
  '7439-96-5', // Manganese
  '7439-97-6', // Mercury
  '7440-02-0', // Nickel
  '7782-49-2', // Selenium
  '7440-22-4', // Silver
  '7440-28-0', // Thallium
  '7440-62-2', // Vanadium
  // For purposes of 6.1 Releases to POTWs, EPA considers it not possible to report Zinc in fume or dust form as that
  // would require it to be in a different form. For purposes of 6.2 EPA does not seem to consider this a metal, and
  // will reject you if you select metal-specific waste codes. I can't find this documented anywhere other than EPA's
  // TRI-ME system rejected an upload with M41 (a metal-specific waste code) and gave me the list of non-metal waste
  // codes in TRI-ME's test environment:
  // '7440-66-6', // Zinc (fume or dust)
] as CasOrCategory[]);

const elementToLowestWeightOxide: Partial<Record<string, string>> = {
  As: 'As2O3', // Arsenic
  Cr: 'Cr2O3', // Chromium
  Co: 'CoO', // Cobalt
  Pb: 'PbO', // Lead
  Hg: 'HgO', // Mercury
  Sb: 'Sb2O3', // Antimony
  Ba: 'BaO', // Barium
  Be: 'BeO', // Beryllium
  Cd: 'CdO', // Cadmium
  Cu: 'CuO', // Copper
  Mn: 'MnO2', // Manganese
  Ni: 'NiO', // Nickel
  Se: 'SeO2', // Selenium
  Ag: 'Ag2O', // Silver
  Tl: 'Tl2O3', // Thallium
  V: 'VO2', // Vanadium
  Zn: 'ZnO', // Zinc
};

// TODO: 3. Release Calculations - These might belong in the database:
const knownNitrates: Partial<Record<string, string>> = {
  '10124-37-5': 'CaN2O6', // Calcium nitrate
  '13477-34-4': 'CaH8N2O10', // Calcium nitrate tetrahydrate
  '7790-69-4': 'LiNO3', // Lithium nitrate
  '7631-99-4': 'NaNO3', // Sodium nitrate
};

export function compoundToElementalMixPercentageConvert(args: ReleaseProfileProcessChemical): number | null {
  const element = categoryToElement[args.casOrCategoryMapped];
  if (!element) {
    return null;
  }

  const lowestWeightOxide = elementToLowestWeightOxide[element];
  const nitrateCompound = knownNitrates[args.casOrCategoryOriginal];
  if (!lowestWeightOxide && !nitrateCompound) {
    return null;
  }

  let min = args.concentrationMin;
  let max = args.concentrationMax;
  if (args.concentrationSolo !== null) {
    min = args.concentrationSolo;
    max = args.concentrationSolo;
  }
  if (min === null) {
    min = args.concentrationForCalculation;
  }
  if (max === null) {
    max = args.concentrationForCalculation;
  }

  let compound: string;
  let moleRatio: number;
  if (lowestWeightOxide) {
    compound = lowestWeightOxide;
    moleRatio = moleRatioFromElementAndFormulas({ element, compoundFormula: lowestWeightOxide });
  } else if (nitrateCompound) {
    compound = nitrateCompound;
    moleRatio = moleRatioFromTwoCompounds({ baseFormula: 'NO3', compoundFormula: nitrateCompound });
  } else {
    return null;
  }

  const minNew = compoundToElementalConcentration({
    compoundConcentration: min,
    elementGramsPerMole: molecularFormulaToGramsPerMoleOrThrow(element),
    compoundGramsPerMole: molecularFormulaToGramsPerMoleOrThrow(compound),
    moleRatio,
  });
  const maxNew = compoundToElementalConcentration({
    compoundConcentration: max,
    elementGramsPerMole: molecularFormulaToGramsPerMoleOrThrow(element),
    compoundGramsPerMole: molecularFormulaToGramsPerMoleOrThrow(compound),
    moleRatio,
  });

  return (minNew + maxNew) / 2;
}

export function elementalToCompoundMixPercentageConvert(args: ReleaseProfileProcessChemical): number | null {
  const element = categoryToElement[args.casOrCategoryMapped];
  if (!element) {
    return null;
  }

  const lowestWeightOxide = elementToLowestWeightOxide[element];
  const nitrateCompound = knownNitrates[args.casOrCategoryOriginal];
  if (!lowestWeightOxide && !nitrateCompound) {
    return null;
  }

  let min = args.concentrationMin;
  let max = args.concentrationMax;
  if (args.concentrationSolo !== null) {
    min = args.concentrationSolo;
    max = args.concentrationSolo;
  }
  if (min === null) {
    min = args.concentrationForCalculation;
  }
  if (max === null) {
    max = args.concentrationForCalculation;
  }

  let compound: string;
  let moleRatio: number;
  if (lowestWeightOxide) {
    compound = lowestWeightOxide;
    moleRatio = moleRatioFromElementAndFormulas({ element, compoundFormula: lowestWeightOxide });
  } else if (nitrateCompound) {
    compound = nitrateCompound;
    moleRatio = moleRatioFromTwoCompounds({ baseFormula: 'NO3', compoundFormula: nitrateCompound });
  } else {
    return null;
  }

  const minNew = elementalToCompoundConcentration({
    elementalConcentration: min,
    elementGramsPerMole: molecularFormulaToGramsPerMoleOrThrow(element),
    compoundGramsPerMole: molecularFormulaToGramsPerMoleOrThrow(compound),
    moleRatio,
  });
  const maxNew = elementalToCompoundConcentration({
    elementalConcentration: max,
    elementGramsPerMole: molecularFormulaToGramsPerMoleOrThrow(element),
    compoundGramsPerMole: molecularFormulaToGramsPerMoleOrThrow(compound),
    moleRatio,
  });

  return (minNew + maxNew) / 2;
}

export function usageMixPercentagesCompute(productUsage: ProductWithChemicalsForUsageMix[]): UsageMixComputationResult {
  const poundsPerCategory: Map<CasOrCategory, number> = new Map();
  let totalPoundsUsed = 0;

  const separateConcentrations = new Map<CasOrCategory, number>();
  const productsUsed = new Set<ProductID>();
  const productConcentrations: UsageMixComputationResult['productConcentrations'] = [];

  for (const product of productUsage) {
    if (product.totalPoundsUsed > 0) {
      productsUsed.add(product.productID);
    }

    const perProduct: Partial<Record<CasOrCategory, number>> = {};

    if (product.type === 'integrated') {
      totalPoundsUsed += product.totalPoundsUsed;

      for (const chemical of product.chemicals) {
        let concentration: number = chemical.concentrationForCalculation;
        if (chemical.elementalConversionEligible) {
          concentration = compoundToElementalMixPercentageConvert(chemical) ?? chemical.concentrationForCalculation;
        }

        perProduct[chemical.casOrCategoryMapped] = (perProduct[chemical.casOrCategoryMapped] ?? 0) + concentration;

        poundsPerCategory.set(
          chemical.casOrCategoryMapped,
          (poundsPerCategory.get(chemical.casOrCategoryMapped) ?? 0) + concentration * product.totalPoundsUsed,
        );
      }
    } else if (product.type === 'separate') {
      for (const chemical of product.chemicals) {
        let concentration: number = chemical.concentrationForCalculation;
        if (chemical.elementalConversionEligible) {
          concentration = compoundToElementalMixPercentageConvert(chemical) ?? chemical.concentrationForCalculation;
        }

        perProduct[chemical.casOrCategoryMapped] = (perProduct[chemical.casOrCategoryMapped] ?? 0) + concentration;

        separateConcentrations.set(
          chemical.casOrCategoryMapped,
          (separateConcentrations.get(chemical.casOrCategoryMapped) ?? 0) +
            concentration * product.remainingInMixPercentage,
        );
      }
    } else {
      const unexpected: never = product;
      console.error(`usageMixPercentagesCompute: Encountered unexpected product type: ${JSON.stringify(unexpected)}`);
    }

    if (Object.keys(perProduct).length > 0) {
      productConcentrations.push({ productID: product.productID, concentrations: perProduct });
    }
  }

  const concentrationReturning: UsageMixComputationResult['percentages'] = {};
  for (const [cas, pounds] of poundsPerCategory.entries()) {
    concentrationReturning[cas] = pounds / totalPoundsUsed;
  }

  for (const [cas, percentage] of separateConcentrations.entries()) {
    concentrationReturning[cas] = (concentrationReturning[cas] ?? 0) + percentage;
  }

  return { productsUsed: Array.from(productsUsed), percentages: concentrationReturning, productConcentrations };
}
